// triggers.tsx
import React, { useEffect, useState, useRef } from 'react';
import { useApiData } from '../../contexts/apiService';
import './triggers.css';
import Toggle from '../../components/common/Toggle'
import ReportButton from './customerReportItem';
import Dropdown from '../../components/common/dropdown/dropdown';
import { useUserAccess, USER_ROLE_LEVEL } from '../../contexts/userAccess';
import { useDataManager } from "../../contexts/dataManager";

interface TriggersProps {
  // Add props here as needed
}

const Button: React.FC<{ onClick: () => void; children: React.ReactNode; className?: string }> = ({ onClick, children, className = '' }) => (
    <button
        onClick={onClick}
        className={`px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 ${className}`}
    >
        {children}
    </button>
);

const TriggersPage: React.FC<TriggersProps> = () => {
    const { TriggerDailyUpdate, TriggerCustomerReport, apiData, processingProgress } = useApiData();
    const { requestTableData, hasTableData } = useDataManager();

    const titleRef = useRef<HTMLDivElement>(null);

    const [isAllModePickup, setIsAllModePickup] = useState(false);
    const [isAllModeDelivery, setIsAllModeDelivery] = useState(false);
    const [isVacMasterMode, setIsVacMasterMode] = useState(false);

    const [isVacSyncing, setIsVacSyncing] = useState(false);
    const [isApRebuilding, setIsApRebuilding] = useState(false);
    const [isBackfillingRegions, setIsBackfillingRegions] = useState(false);
    const [isNglRecalculating, setIsNglRecalculating] = useState(false);
    const [scheduleToggle, setScheduleToggle] = useState(false);
    const [reportsTable, setReportsTable] = useState<Array<any> | undefined>(undefined);
    const [producerName, setProducerName] = useState("");
    const [ticketNumber, setTicketNumber] = useState("");
    const [showProducerReports, setShowProducerReports] = useState(false);
    const [showMarketReports, setShowMarketReports] = useState(false);
    const [showInternalReports, setShowInternalReports] = useState(false);
    const [showWeeklyProducer, setShowWeeklyProducer] = useState(false);


    const [RIDNumber, setRIDNumber] = useState("");

    const { userRole = 'viewer', hasAdminAccess } = useUserAccess();
    const ADMIN_LEVEL = USER_ROLE_LEVEL['accounting_admin'];
    const showTable =  Number(Number(USER_ROLE_LEVEL[userRole])) >= ADMIN_LEVEL;


    useEffect(() => {
        if(!hasTableData('customer_reports')) {
            requestTableData('customer_reports');
        }
        const reportsTable = apiData?.customer_reports
        setReportsTable(reportsTable)
    }, [apiData]);

    const handleMainTriggerPickup = async () => {
        try {
            let trigger_mode = 'default'
            if (isAllModePickup) {
                TriggerDailyUpdate('daily_update', 'monthly', selectedMonthMain);
            } else {
                TriggerDailyUpdate('daily_update', trigger_mode);
            }
        } catch (error) {
            console.error('Error in trigger:', error);
        }
    };

    // const handleMigrationTrigger = async () => {
    //     try {
    //         if (!selectedMonthRoute) {
    //             alert("Please enter a month for data migration");
    //         }
    //         TriggerDailyUpdate('data_migration', 'route_processing', selectedMonthRoute);
    //     } catch (error) {
    //         console.error('Error in trigger:', error);
    //     }
    // }

    const handleVacApRebuild = async () => {
        if (isApRebuilding) return;
        setIsApRebuilding(true);
        try {
            await TriggerDailyUpdate('vac_sync', 'rebuild_ap');
        } catch (error) {
            console.error('Error in AP rebuild trigger:', error);
        } finally {
            setIsApRebuilding(false);
        }
    };

    const handleVacSyncTrigger = async () => {
        if (isVacSyncing) return;
        setIsVacSyncing(true);
        try {
            if (isVacMasterMode) {
                await TriggerDailyUpdate('vac_sync', 'master', selectedMonthVac);
            } else {
                await TriggerDailyUpdate('vac_sync', 'daily');
            }
        } catch (error) {
            console.error('Error in vac sync trigger:', error);
        } finally {
            setIsVacSyncing(false);
        }
    };

    const handleBackfillLeaseRegions = async () => {
        if (isBackfillingRegions) return;
        setIsBackfillingRegions(true);
        try {
            await TriggerDailyUpdate('backfill_lease_regions', 'run');
        } catch (error) {
            console.error('Error in backfill lease regions trigger:', error);
        } finally {
            setIsBackfillingRegions(false);
        }
    };

    const handleNglRecalculate = async () => {
        if (isNglRecalculating) return;
        setIsNglRecalculating(true);
        try {
            await TriggerDailyUpdate('ngl_schedule_recalculate', 'month', selectedMonthNgl);
        } catch (error) {
            console.error('Error in NGL recalculation trigger:', error);
        } finally {
            setIsNglRecalculating(false);
        }
    };

    const handleTicketTrigger = async () => {
        try {
            if (ticketNumber.trim()) {
                TriggerDailyUpdate('daily_update', 'ticket_number', ticketNumber.trim());
            } else {
                alert("Please enter a ticket number");
            }
        } catch (error) {
            console.error('Error in ticket trigger:', error);
        }
    };

    const handleRIDTrigger = async () => {
        try {
            if (RIDNumber.trim()) {
                TriggerDailyUpdate('daily_update', 'rid', RIDNumber.trim());
            } else {
                alert("Please enter a RID number");
            }
        } catch (error) {
            console.error('Error in RID trigger:', error);
        }
    };

    const handleCustomerReport = (customer: string, mailingList: string, currentMonth: boolean, selectedMonth: string) => {
        TriggerCustomerReport(customer, mailingList, currentMonth, selectedMonth);
    };

    const generateMonthOptions = () => {
        const months = [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ];
        
        const today = new Date();
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth(); // 0-based
        
        const options = [];
        
        // Start from current month/year and go backwards
        for (let year = currentYear; year >= 2021; year--) {
            const endMonth = year === currentYear ? currentMonth : 11;
            const startMonth = year === 2021 ? 11 : 0; // Start from December 2021
            
            for (let month = endMonth; month >= startMonth; month--) {
                const monthYear = `${months[month]} ${year}`;
                options.push({
                    value: monthYear,
                    label: monthYear
                });
            }
        }
        
        return options;
    };

    const monthOptions = generateMonthOptions();
    const [selectedMonthMain, setSelectedMonthMain] = useState(monthOptions[0]?.value || "");
    const [selectedMonthVac, setSelectedMonthVac] = useState(monthOptions[0]?.value || "");
    const [selectedMonthNgl, setSelectedMonthNgl] = useState(monthOptions[0]?.value || "");

    const [selectedMonthRoute, setSelectedMonthRoute] = useState(monthOptions[0]?.value || "");

    if (!showTable) {
        return <p>Not Allowed</p>;
    }

    const renderProducerReports = (type: string) => {
        if (!reportsTable) return null;

        const reports = reportsTable
            .filter(reportData => 
                type === 'producer' ? reportData.is_internal === 0 && reportData.is_market !== 1 :
                type === 'market' ? reportData.is_internal === 0 && reportData.is_market === 1 :
                type === 'internal' ? reportData.is_internal === 1 : false
            )
            .map(reportData => (
                <ReportButton
                    key={reportData.id}
                    reportName={reportData.customer}
                    onTrigger={(mailingList, currentMonth, selectedMonth) => 
                        handleCustomerReport(reportData.customer, mailingList, currentMonth, selectedMonth)
                    }
                    reportData={reportData}
                    isLoading={!reportsTable}
                    hasAdminAccess={hasAdminAccess}
                />
            ));
        
        const handleToggle = () => {
            // Scroll up when hiding
            titleRef.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
            if (type === 'producer') {
                setShowProducerReports(!showProducerReports);
            } else if (type === 'market') {
                setShowMarketReports(!showMarketReports);
            } else if (type === 'internal') {
                setShowInternalReports(!showInternalReports);
            }
        };
        
        return (
            <>
                {type === 'internal' && (
                    <ReportButton
                        reportName="Nominations"
                        onTrigger={(mailingList, currentMonth, selectedMonth) => handleCustomerReport('Nominations', mailingList, currentMonth, selectedMonth)}
                        reportData={reportsTable?.find(report => report.customer === 'Nominations')}
                        isLoading={!reportsTable}
                        hasAdminAccess={hasAdminAccess}
                    />
                )}
                {reports}
                <button 
                    className="triggers-toggleButton"
                    onClick={handleToggle}
                >
                    <span className={`triggers-arrow ${showProducerReports ? 'triggers-arrow-expanded' : ''}`}>
                        ▶
                    </span>
                    <span className="triggers-toggleText">
                        {
                            type === 'producer' ? 'Hide producer reports' : 
                            type === 'market' ? 'Hide market reports' :
                            type === 'internal' ? 'Hide internal reports' :
                            null
                        }
                    </span>
                </button>

            </>
        );
    };

    return (
        <div className="triggers-mainContainer">
            <div className="triggers-titleContainer" ref={titleRef}>
                <h1 className="text-2xl font-semibold">Triggers</h1>
            </div> 

            <div className="triggers-buttonContainer">
                <Button onClick={handleMainTriggerPickup}>
                    Main Processing Pickup
                </Button>
                <Toggle 
                    checked={isAllModePickup} 
                    onChange={setIsAllModePickup}
                    labelOn={'Monthly'}
                    labelOff={'Daily'}
                />
                {isAllModePickup && (
                    <Dropdown
                        options={monthOptions}
                        value={selectedMonthMain}
                        onChange={setSelectedMonthMain}
                        placeholder="Select month and year"
                    />
                )}
                {processingProgress && (
                    <div className="processing-status">
                        <div>
                            {processingProgress.status}                        
                        </div>
                        <div>
                            {processingProgress.stage}
                        </div>
                    </div>
                )}
            </div>

            <div className="triggers-buttonContainer">
                <Button onClick={handleVacSyncTrigger} className={isVacSyncing ? 'opacity-50 cursor-not-allowed' : ''} >
                    {isVacSyncing ? 'Syncing…' : 'Vac Truck OI Sync'}
                </Button>
                <Button onClick={handleVacApRebuild} className={isApRebuilding ? 'opacity-50 cursor-not-allowed' : ''} >
                    {isApRebuilding ? 'Rebuilding…' : 'Rebuild AP Staging'}
                </Button>
                <Toggle
                    checked={isVacMasterMode}
                    onChange={setIsVacMasterMode}
                    labelOn={'Master (Monthly)'}
                    labelOff={'Daily'}
                />
                {isVacMasterMode && (
                    <Dropdown
                        options={monthOptions}
                        value={selectedMonthVac}
                        onChange={setSelectedMonthVac}
                        placeholder="Select month and year"
                    />
                )}
            </div>

            <div className="triggers-buttonContainer">
                <Button onClick={handleBackfillLeaseRegions} className={isBackfillingRegions ? 'opacity-50 cursor-not-allowed' : ''}>
                    {isBackfillingRegions ? 'Backfilling…' : 'Backfill Lease Regions'}
                </Button>
            </div>

            <div className="triggers-buttonContainer">
                <Button onClick={handleNglRecalculate} className={isNglRecalculating ? 'opacity-50 cursor-not-allowed' : ''}>
                    {isNglRecalculating ? 'Recalculating…' : 'Recalculate NGL Composition'}
                </Button>
                <Dropdown
                    options={monthOptions}
                    value={selectedMonthNgl}
                    onChange={setSelectedMonthNgl}
                    placeholder="Select month and year"
                />
            </div>

            <div className="triggers-buttonContainer">
                <Button onClick={handleTicketTrigger}>
                    Ticket Processing
                </Button>
                <input
                    type="text"
                    value={ticketNumber}
                    onChange={(e) => setTicketNumber(e.target.value)}
                    placeholder="Enter ticket number"
                    className="px-3 py-2 border border-gray-300 rounded-md mr-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
            </div>

            <div className="triggers-buttonContainer">
                <Button onClick={handleRIDTrigger}>
                    RID Processing
                </Button>
                <input
                    type="text"
                    value={RIDNumber}
                    onChange={(e) => setRIDNumber(e.target.value)}
                    placeholder="Enter RID number"
                    className="px-3 py-2 border border-gray-300 rounded-md mr-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
            </div>
            
            {/* <div className="triggers-buttonContainer">
                <Button onClick={handleMigrationTrigger}>
                    Data Migration
                </Button>
                <Dropdown
                    options={monthOptions}
                    value={selectedMonthRoute}
                    onChange={setSelectedMonthRoute}
                    placeholder="Select month and year"
                />
            </div> */}

            <div className="triggers-titleContainer">
                <h1 className="text-2xl font-semibold">Weekly Settlements</h1>
            </div>
            <div className="triggers-sectionContainer">
                <div className="triggers-sectionHeader">
                    <h1 className="text-xl font-semibold">Producer Reports</h1>
                    <button
                        className="triggers-toggleButton"
                        onClick={() => setShowWeeklyProducer(!showWeeklyProducer)}
                    >
                        <span className={`triggers-arrow ${showWeeklyProducer ? 'triggers-arrow-expanded' : ''}`}>▶</span>
                        <span className="triggers-toggleText">{showWeeklyProducer ? 'Show less' : 'See more'}</span>
                    </button>
                </div>
                <div className={`triggers-collapsibleContent ${showWeeklyProducer ? 'triggers-expanded' : ''}`}>
                    {['Blackbuck', 'Layne Water'].map(producer => (
                        <ReportButton
                            key={producer}
                            reportName={`${producer} Weekly Settlement`}
                            onTrigger={(mailingList, currentMonth, selectedMonth) =>
                                TriggerCustomerReport(producer, mailingList, currentMonth, selectedMonth, 'weekly_settlement')
                            }
                            reportData={reportsTable?.find(r => r.customer === producer)}
                            isLoading={!reportsTable}
                            hasAdminAccess={hasAdminAccess}
                        />
                    ))}
                </div>
            </div>

            <div className="triggers-titleContainer">
                <h1 className="text-2xl font-semibold">Daily Reports</h1>
            </div>
            <div className="triggers-sectionContainer">
                <div className="triggers-sectionHeader">
                    <h1 className="text-xl font-semibold">Internal Reports</h1>
                    <button
                        className="triggers-toggleButton"
                        onClick={() => setShowInternalReports(!showInternalReports)}
                    >
                        <span className={`triggers-arrow ${showInternalReports ? 'triggers-arrow-expanded' : ''}`}>
                            ▶
                        </span>
                        <span className="triggers-toggleText">
                            {showInternalReports ? 'Show less' : 'See more'}
                        </span>
                    </button>
                </div>
                <div className={`triggers-collapsibleContent ${showInternalReports ? 'triggers-expanded' : ''}`}>
                    {renderProducerReports('internal')}
                </div>
            </div>

            <div className="triggers-sectionContainer">
                <div className="triggers-sectionHeader">
                    <h1 className="text-xl font-semibold">Producer Reports</h1>
                    <button
                        className="triggers-toggleButton"
                        onClick={() => setShowProducerReports(!showProducerReports)}
                    >
                        <span className={`triggers-arrow ${showProducerReports ? 'triggers-arrow-expanded' : ''}`}>
                            ▶
                        </span>
                        <span className="triggers-toggleText">
                            {showProducerReports ? 'Show less' : 'See more'}
                        </span>
                    </button>
                </div>
                <div className={`triggers-collapsibleContent ${showProducerReports ? 'triggers-expanded' : ''}`}>
                    {renderProducerReports('producer')}
                </div>
            </div>

            <div className="triggers-sectionContainer">
                <div className="triggers-sectionHeader">
                    <h1 className="text-xl font-semibold">Market Reports</h1>
                    <button 
                        className="triggers-toggleButton"
                        onClick={() => setShowMarketReports(!showMarketReports)}
                    >
                        <span className={`triggers-arrow ${showMarketReports ? 'triggers-arrow-expanded' : ''}`}>
                            ▶
                        </span>
                        <span className="triggers-toggleText">
                            {showMarketReports ? 'Show less' : 'See more'}
                        </span>
                    </button>
                </div>
                <div className={`triggers-collapsibleContent ${showMarketReports ? 'triggers-expanded' : ''}`}>
                    {renderProducerReports('market')}
                </div>
            </div>
        </div>
    );
};
  
export default TriggersPage;
